import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api';
import { stringifyError } from '@/lib/errors';

export function stripUrlSearchParams(keys: string[]): void {
  const url = new URL(window.location.href);
  for (const key of keys) {
    url.searchParams.delete(key);
  }
  window.history.replaceState({}, '', url.pathname + url.search);
}

/** Alias for stripUrlSearchParams. */
export const stripDeepLinkParams = stripUrlSearchParams;

export function replaceBrowserUrl(pathWithSearch: string): void {
  window.history.replaceState({}, '', pathWithSearch);
}

export type EntityDeepLinkResult = 'found' | 'missing' | 'skipped';

export type ResolveEntityDeepLinkOptions<T> = {
  loading: boolean;
  id: number | string | null | undefined;
  handledRef: { current: boolean };
  findInList?: () => T | null | undefined;
  fetchById?: () => Promise<T | null | undefined>;
  onFound: (entity: T) => void;
  onMissing?: (id: number | string) => void;
  missingTitle: string;
  missingDescription?: (id: number | string) => string;
  stripKeys?: string[];
  replaceUrl?: () => string;
};

function normalizeDeepLinkId(id: number | string | null | undefined): number | string | null {
  if (typeof id === 'number') return Number.isFinite(id) && id > 0 ? id : null;
  const trimmed = String(id ?? '').trim();
  return trimmed ? trimmed : null;
}

/**
 * One-shot deep-link bootstrap: resolve entity from list or API, toast + strip URL on missing.
 */
export async function resolveEntityDeepLink<T>(
  options: ResolveEntityDeepLinkOptions<T>,
): Promise<EntityDeepLinkResult> {
  const {
    loading,
    id,
    handledRef,
    findInList,
    fetchById,
    onFound,
    onMissing,
    missingTitle,
    missingDescription,
    stripKeys,
    replaceUrl,
  } = options;

  if (loading) return 'skipped';
  const normalized = normalizeDeepLinkId(id);
  if (!normalized || handledRef.current) return 'skipped';

  handledRef.current = true;

  try {
    const fromList = findInList?.();
    if (fromList != null) {
      onFound(fromList);
      return 'found';
    }
    if (fetchById) {
      const fetched = await fetchById();
      if (fetched != null) {
        onFound(fetched);
        return 'found';
      }
    }
  } catch (err) {
    toast({
      title: missingTitle,
      description:
        missingDescription?.(normalized) ??
        (err instanceof ApiError && err.status === 404
          ? `Could not find ${normalized}.`
          : stringifyError(err)),
      variant: 'destructive',
    });
    onMissing?.(normalized);
    if (replaceUrl) replaceBrowserUrl(replaceUrl());
    else if (stripKeys?.length) stripUrlSearchParams(stripKeys);
    return 'missing';
  }

  toast({
    title: missingTitle,
    description: missingDescription?.(normalized) ?? `Could not find ${normalized}.`,
    variant: 'destructive',
  });
  onMissing?.(normalized);
  if (replaceUrl) replaceBrowserUrl(replaceUrl());
  else if (stripKeys?.length) stripUrlSearchParams(stripKeys);
  return 'missing';
}
